import { Button } from '@components/Button/';
import EVImg from '@components/EVImg/EVImg';
import Block from '@components/Layout/Block';
import styled from 'styled-components';

import { Props } from '@components/BlockMapper';
import AspectBox from '@components/Layout/AspectBox';
import RichText from '@components/RichText';
import { color, font, query, space } from '@design/templateFns';
import { FC } from 'react';
import { getTestId } from '@util/getTestIds';

/*** STYLING ***/

const WideStackedTextImageBlock: FC<Props> = (props) => {
  const {
    imgFocalPoint,
    eyebrow,
    header,
    alt_text,
    richText,
    links,
    eyebrowSidebar,
    headerSidebar,
    bodySidebar,
    linksSidebar,
    backgroundColor,
    reverse,
    className,
  } = props;
  const dataTestId = eyebrowSidebar ? getTestId(eyebrowSidebar) : '';

  return (
    <Block fullOnMobile className={className}>
      <Container $reverse={reverse} data-testid={`${dataTestId}-container`}>
        <GridLeftContainer data-testid={`${dataTestId}-left-container`}>
          <SideTextContainer
            data-testid={`${dataTestId}-left-text`}
            $backgroundColor={backgroundColor}
            $reverse={reverse}
          >
            {eyebrow && (
              <Eyebrow
                data-testid={`${dataTestId}-left-eyebrow`}
                $backgroundColor={backgroundColor}
              >
                {eyebrow}
              </Eyebrow>
            )}
            {header && (
              <Header data-testid={`${dataTestId}-left-header`} $backgroundColor={backgroundColor}>
                {header}
              </Header>
            )}
            {richText && (
              <Text
                $backgroundColor={backgroundColor}
                data-testid={`${dataTestId}-left-description`}
              >
                <RichText richText={richText} />
              </Text>
            )}
            {links &&
              links.map((link, index) => {
                //check if the contentType is linkDownload
                if (link?.sys?.contentType?.sys?.id === 'linkDownload') {
                  return (
                    <Button
                      target={link?.fields?.external}
                      variant={backgroundColor === false ? 'tertiaryBlack' : 'tertiaryWhite'}
                      label={link?.fields?.text}
                      url={link?.fields?.url}
                      arrow={false}
                      icon="Download"
                      iconPosition="right"
                      dataTestId={`${dataTestId}-left-cta-${index}`}
                    />
                  );
                } else {
                  return (
                    <div key={index}>
                      <Button
                        target={link?.fields?.external}
                        arrow={true}
                        variant={backgroundColor === false ? 'tertiaryBlack' : 'tertiaryWhite'}
                        url={link?.fields?.url}
                        label={link?.fields?.text}
                        dataTestId={`${dataTestId}-left-cta`}
                      />
                    </div>
                  );
                }
              })}
          </SideTextContainer>
        </GridLeftContainer>
        <GridRightContainer $reverse={reverse} data-testid={`${dataTestId}-right-container`}>
          {imgFocalPoint && (
            <ImageBox $ratio={[1.79]}>
              <EVImg imageFocalPointData={imgFocalPoint} alt={alt_text} />
            </ImageBox>
          )}
          {/* </Scrunch> */}
          {(eyebrowSidebar || headerSidebar || bodySidebar) && (
            <BottomTextContainer $backgroundColor={backgroundColor}>
              <BottomBlock>
                {eyebrowSidebar && (
                  <BottomEyebrow
                    data-testid={`${dataTestId}-right-eyebrow`}
                    $backgroundColor={backgroundColor}
                  >
                    {eyebrowSidebar}
                  </BottomEyebrow>
                )}
                {headerSidebar && (
                  <BottomHeader
                    data-testid={`${dataTestId}-right-header`}
                    $backgroundColor={backgroundColor}
                  >
                    {headerSidebar}
                  </BottomHeader>
                )}
              </BottomBlock>
              <BottomBlock>
                {bodySidebar && (
                  <BottomText
                    data-testid={`${dataTestId}-right-description`}
                    $backgroundColor={backgroundColor}
                  >
                    <RichText richText={bodySidebar} />
                  </BottomText>
                )}
                {linksSidebar &&
                  linksSidebar.map((link, index) => {
                    //check if the contentType is linkDownload
                    if (link?.sys?.contentType?.sys?.id === 'linkDownload') {
                      return (
                        <DLWrapper>
                          <Button
                            variant={backgroundColor === true ? 'tertiaryBlack' : 'tertiaryWhite'}
                            target={link?.fields?.external}
                            label={link?.fields?.text}
                            url={link?.fields?.url}
                            arrow={false}
                            icon="Download"
                            iconPosition="right"
                            dataTestId={`${dataTestId}-right-cta-${index}`}
                          />
                        </DLWrapper>
                      );
                    } else {
                      return (
                        <div key={index}>
                          <Button
                            arrow={true}
                            variant={backgroundColor === true ? 'tertiaryBlack' : 'tertiaryWhite'}
                            target={link?.fields?.external}
                            url={link?.fields?.url}
                            label={link?.fields?.text}
                            dataTestId={`${dataTestId}-right-cta`}
                          />
                        </div>
                      );
                    }
                  })}
              </BottomBlock>
            </BottomTextContainer>
          )}
        </GridRightContainer>
      </Container>
    </Block>
  );
};

export default WideStackedTextImageBlock;

export const Container = styled.div<{ $reverse?: boolean }>`
  display: grid;
  gap: ${space('x5')};
  @media screen and (${query.atLeast('desktop')}) {
    grid-template-columns: ${({ $reverse }) => ($reverse ? '2fr 1fr' : '1fr 2fr')};
  }

  grid-gap: ${({ $reverse }) => $reverse && '15px'};
  @media screen and (${query.below('desktop')}) {
    gap: 0;
  }
`;

export const BottomBlock = styled.div`
  display: flex;
  flex-direction: column;
  width: 45%;
  @media screen and (${query.below('desktop')}) {
    width: 100%;
  }
`;

export const GridLeftContainer = styled.div`
  display: grid;
`;

export const GridRightContainer = styled.div<{ $reverse?: boolean }>`
  display: flex;
  flex-direction: column-reverse;
  // margin: auto;
  gap: 0;
  order: ${({ $reverse }) => ($reverse ? '-1' : '0')};
  @media screen and (${query.atLeast('tablet')}) {
    flex-direction: column;
  }
`;

export const SideTextContainer = styled.div<{
  $reverse?: boolean;
  $backgroundColor?: boolean | string;
}>`
  display: flex;
  padding: 64px 20px;
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor ? color('fg') : color('secondaryBg')};
  flex-direction: column;
  grid-row: 2;
  justify-content: flex-start;
  a span {
    justify-content: left;
  }
  @media screen and (${query.atLeast('tablet')}) {
    margin-top: 0;
    padding: 64px 47px;
    grid-row: ${({ $reverse }) => ($reverse ? 1 : 'unset')};
  }
`;

export const BottomTextContainer = styled.div<{
  $backgroundColor?: boolean | string;
}>`
  display: flex;
  color: ${color('bg')};
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor ? color('secondaryBg') : color('fg')};
  flex-direction: row;
  justify-content: space-evenly;
  a span {
    justify-content: left;
  }
  padding: 44px 20px;
  @media screen and (${query.atLeast('tablet')}) {
    margin-top: 0;
    padding: 64px;
  }
  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

export const ContentContainer = styled.div<{ $reverse?: boolean }>`
  display: flex;
  justify-content: center;
  flex-direction: column;
  grid-row: 2;
  text-align: center;

  @media screen and (${query.atLeast('tablet')}) {
    margin-top: 0;
    grid-row: ${({ $reverse }) => ($reverse ? 1 : 'unset')};
  }
`;

export const Eyebrow = styled.h3<{ $backgroundColor?: boolean | string }>`
  text-transform: uppercase;
  color: ${color('cta')};
  ${font('eyebrow-md')};
  padding-bottom: 16px;
  margin-top: 0;
  font-size: 12px;
  color: ${({ $backgroundColor }) => ($backgroundColor === true ? color('gray2') : color('cta'))};

  @media screen and (${query.atLeast('tablet')}) {
    font-size: 14px;
  }
`;

export const Header = styled.h2<{ $backgroundColor?: boolean | string }>`
  margin: 0px 0 32px;
  ${font('headline-sm')};
  padding: 0;
  color: ${({ $backgroundColor }) => ($backgroundColor === true ? color('bg') : color('fg'))};
  @media screen and (${query.below('tablet')}) {
    font-size: 32px;
  }
`;

export const Text = styled.div<{ $backgroundColor?: boolean | string }>`
  color: ${color('fg')};
  padding-bottom: 0;
  ${font('body-md-light')};
  color: ${({ $backgroundColor }) => ($backgroundColor === true ? color('bg') : color('fg'))};
`;

export const BottomEyebrow = styled.h3<{ $backgroundColor?: boolean | string }>`
  ${font('eyebrow-sm')};
  text-transform: uppercase;
  padding-bottom: 16px;
  color: ${({ $backgroundColor }) => ($backgroundColor === true ? color('cta') : color('gray2'))};
  @media screen and (${query.atLeast('tablet')}) {
    font-size: 14px;
  }
`;

export const BottomHeader = styled.h2<{ $backgroundColor?: boolean | string }>`
  ${font('headline-sm')};
  color: ${({ $backgroundColor }) => ($backgroundColor === true ? color('fg') : color('bg'))};
  margin-top: 16px;
  padding: 0;
  @media screen and (${query.below('tablet')}) {
    ${font('headline-xxs')};
  }
`;

export const BottomText = styled.div<{ $backgroundColor?: boolean | string }>`
  ${font('body-md-light')};
  color: ${({ $backgroundColor }) => ($backgroundColor === true ? color('fg') : color('bg'))};
  padding-bottom: 0;
  @media screen and (${query.below('tablet')}) {
    ${font('body-sm-light')};
  }
`;

export const ImageBox = styled(AspectBox).attrs({ $ratio: [1.79] })`
  flex: 1;
  span {
    height: 100%;
  }
`;

export const DLWrapper = styled.div``;
